/**
 * Application Providers - Composes all React context providers for the application.
 * @agentic connections
 */

import { ReactNode } from "react";
import { Box, MantineProvider, Text } from "@mantine/core";
import { MedplumProvider } from "@medplum/react";
import { Notifications } from "@mantine/notifications";
import { ErrorBoundary } from "react-error-boundary";
import { benchmarkTheme } from "@benchmark/ui/theme";
import { toLogSafeError } from "@benchmark/core/utils";
import type { MedplumClient } from "@medplum/core";
import type { FallbackProps } from "react-error-boundary";
import { reportReactError } from "@/app/boot/initializeDatadog";
import { AuthSessionProvider } from "@/app/auth/AuthSessionContext";

/**
 * Global error fallback shown when an unrecoverable error occurs.
 * @agentic connections
 */
const GlobalErrorFallback = ({ resetErrorBoundary }: FallbackProps) => (
  <Box
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      gap: "1rem",
      padding: "2rem",
    }}
  >
    <Text fw={600} size="lg">
      Something went wrong
    </Text>
    <Text c="dimmed" size="sm" ta="center" maw={400}>
      An unexpected error occurred. The error has been reported and our team
      will investigate.
    </Text>
    {/* The raw error message is intentionally not rendered: a Medplum error can
        embed FHIR request URLs with patient search terms (#1187). It is
        reported (sanitized) via reportReactError instead. */}
    <button
      onClick={resetErrorBoundary}
      style={{
        padding: "0.5rem 1.5rem",
        borderRadius: "4px",
        border: "1px solid #ccc",
        background: "#fff",
        cursor: "pointer",
        marginTop: "0.5rem",
      }}
    >
      Try Again
    </button>
  </Box>
);

/** Props for AppProviders. @agentic connections */
type AppProvidersProps = {
  children: ReactNode;
  medplum: MedplumClient;
  navigate: (path: string) => Promise<void>;
};

/**
 * Root provider component that wraps the application with all necessary providers.
 * @agentic connections
 */
export const AppProviders = ({
  children,
  medplum,
  navigate,
}: AppProvidersProps) => {
  return (
    <ErrorBoundary
      FallbackComponent={GlobalErrorFallback}
      onError={(error, errorInfo) => {
        // Log only sanitized metadata; the raw error can carry PHI-adjacent
        // request context (#1187). reportReactError handles the (scrubbed)
        // Datadog report, including the component stack.
        console.error("Unhandled application error:", toLogSafeError(error));
        reportReactError(error, errorInfo);
      }}
    >
      <MedplumProvider medplum={medplum} navigate={navigate}>
        <AuthSessionProvider>
          <MantineProvider theme={benchmarkTheme} forceColorScheme="light">
            <Notifications position="top-right" />
            {children}
          </MantineProvider>
        </AuthSessionProvider>
      </MedplumProvider>
    </ErrorBoundary>
  );
};
